<script setup>
// Lead Workspace — Property Match tab. Surfaces the existing
// Nexceo::ProjectMatchingService (multi-factor weighted scoring against real
// project + live unit-inventory data) via PropertyMatchesController — no new
// matching algorithm, and no fabricated 94%/87%/76%-style percentages. The
// score shown is the service's own point total, and `reasons` are its own
// explainable output. Filters/Quick Actions below operate only on this real
// match data — no "Ask AI" free-text re-query (that would be new AI
// automation, out of scope here).
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import leadsAPI from 'dashboard/api/nexceo/leads';
import SiteVisitForm from '../../calendar/SiteVisitForm.vue';
import { formatMoney, humanize } from '../leadFormatters';

const props = defineProps({
  lead: { type: Object, required: true },
  // Lifted to LeadWorkspace.vue so it survives switching tabs and back
  // within the same visit — see that file's shortlist comment.
  shortlist: { type: Array, default: () => [] },
});
const emit = defineEmits(['toggle-shortlist', 'share-documents']);

const router = useRouter();
const { accountId } = useAccount();

const matches = ref([]);
const isLoading = ref(true);
const loadError = ref('');
const statusFilter = ref('');
const isVisitFormOpen = ref(false);

async function load() {
  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await leadsAPI.getPropertyMatches(props.lead.id, 8);
    matches.value = data?.matches ?? [];
  } catch {
    loadError.value = 'Could not load property matches.';
  } finally {
    isLoading.value = false;
  }
}

onMounted(load);

const statusOptions = computed(() => {
  const statuses = new Set(matches.value.map(m => m.status).filter(Boolean));
  return Array.from(statuses);
});

const filteredMatches = computed(() =>
  statusFilter.value ? matches.value.filter(m => m.status === statusFilter.value) : matches.value
);

const bestMatch = computed(() =>
  matches.value.length ? matches.value[0] : null
);

function toggleShortlist(match) {
  emit('toggle-shortlist', match);
}

function isShortlisted(match) {
  return props.shortlist.some(m => m.project_id === match.project_id);
}

// Per the reviewed decision: sharing documents is only ever offered for a
// match that IS the lead's own assigned project — Nexceo::DocumentShareService
// has a deliberate server-side guard blocking any other project's documents
// from being sent to this lead (prevents cross-project data leaks), so a
// mismatched-project match never gets a working share action here.
function isSameProject(match) {
  return !!match?.project_id && match.project_id === props.lead.project_id;
}

function shareMatch(match) {
  if (!isSameProject(match)) return;
  emit('share-documents');
}

function priceRange(m) {
  const min = formatMoney(m.price_min, m.currency);
  const max = formatMoney(m.price_max, m.currency);
  if (min && max) return `${min} – ${max}`;
  return min || max || '—';
}

function viewProject(match) {
  router.push({
    name: 'nexceo_sales_project_show',
    params: { accountId: accountId.value, id: match.project_id, tab: 'overview' },
  });
}

</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid grid-cols-1 lg:grid-cols-[200px_1fr_300px] gap-4 p-4">
    <!-- Filter Properties -->
    <div class="rounded-xl border border-n-weak bg-n-background p-3 h-fit">
      <div class="flex items-center justify-between mb-2 px-1">
        <h3 class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide">
          Filter Properties
        </h3>
        <button
          v-if="statusFilter"
          class="text-[11px] text-woot-600 hover:underline"
          @click="statusFilter = ''"
        >
          Reset
        </button>
      </div>
      <button
        class="w-full text-left px-2 py-1.5 rounded-lg text-sm capitalize"
        :class="
          !statusFilter
            ? 'bg-woot-50 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400 font-medium'
            : 'text-n-slate-11 hover:bg-n-alpha-1'
        "
        @click="statusFilter = ''"
      >
        All ({{ matches.length }})
      </button>
      <button
        v-for="s in statusOptions"
        :key="s"
        class="w-full text-left px-2 py-1.5 rounded-lg text-sm capitalize"
        :class="
          statusFilter === s
            ? 'bg-woot-50 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400 font-medium'
            : 'text-n-slate-11 hover:bg-n-alpha-1'
        "
        @click="statusFilter = s"
      >
        {{ humanize(s) }} ({{ matches.filter(m => m.status === s).length }})
      </button>
    </div>

    <div>
      <div
        class="flex items-center gap-2 rounded-xl border border-n-weak bg-woot-50 dark:bg-woot-500/10 p-3 mb-4"
      >
        <span class="i-lucide-sparkles size-4 text-woot-600 shrink-0" />
        <p class="text-sm text-n-slate-12 flex-1">
          <template v-if="isLoading">Finding matching properties…</template>
          <template v-else-if="matches.length">
            Found {{ matches.length }} matching propert{{ matches.length === 1 ? 'y' : 'ies' }} for this lead, based on budget, configuration and location.
          </template>
          <template v-else>No matching properties found for this lead's requirements yet.</template>
        </p>
        <button
          class="text-xs font-medium text-woot-700 hover:underline shrink-0"
          @click="load"
        >
          Re-run Match
        </button>
      </div>

      <div v-if="loadError" class="text-sm text-red-600">{{ loadError }}</div>

      <div v-else class="space-y-3">
        <div
          v-for="m in filteredMatches"
          :key="m.project_id"
          class="rounded-xl border border-n-weak bg-n-background p-3 flex gap-3"
        >
          <div class="size-20 rounded-lg bg-n-alpha-2 overflow-hidden shrink-0">
            <img
              v-if="m.hero_image_url"
              :src="m.hero_image_url"
              class="w-full h-full object-cover"
              alt=""
            />
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="text-sm font-semibold text-n-slate-12 truncate">{{ m.name }}</p>
                <p class="text-xs text-n-slate-8 flex items-center gap-1">
                  <span class="i-lucide-map-pin size-3" /> {{ m.city || m.location || '—' }}
                </p>
              </div>
              <span
                class="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400 shrink-0"
              >
                {{ m.score }} pts
              </span>
            </div>
            <p class="text-sm text-n-slate-11 mt-1">{{ priceRange(m) }}</p>
            <span
              v-if="m.status"
              class="inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-9 capitalize mt-1"
            >
              {{ humanize(m.status) }}
            </span>
            <ul v-if="m.reasons?.length" class="mt-1.5 space-y-0.5">
              <li
                v-for="(r, i) in m.reasons"
                :key="i"
                class="text-xs text-n-slate-9 flex items-start gap-1"
              >
                <span class="i-lucide-check size-3 text-green-600 shrink-0 mt-0.5" />
                {{ r }}
              </li>
            </ul>
            <div class="flex items-center gap-2 mt-2 flex-wrap">
              <button
                class="text-xs px-2.5 py-1 rounded-lg border border-n-weak text-n-slate-11 hover:border-woot-400 font-medium"
                @click="viewProject(m)"
              >
                View Details
              </button>
              <button
                class="text-xs px-2.5 py-1 rounded-lg font-medium"
                :class="
                  isShortlisted(m)
                    ? 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400'
                    : 'border border-n-weak text-n-slate-11 hover:border-woot-400'
                "
                @click="toggleShortlist(m)"
              >
                {{ isShortlisted(m) ? 'Shortlisted' : 'Add to Shortlist' }}
              </button>
              <button
                v-if="isSameProject(m)"
                class="text-xs px-2.5 py-1 rounded-lg font-medium text-green-700 border border-green-200 hover:bg-green-50 dark:text-green-400 dark:border-green-500/30"
                @click="shareMatch(m)"
              >
                Share Documents
              </button>
              <span v-else class="text-[11px] text-n-slate-7">
                Reassign lead to this project to share documents
              </span>
            </div>
          </div>
        </div>
        <p v-if="!isLoading && !filteredMatches.length" class="text-sm text-n-slate-8">
          Add a budget, property type or preferred location on the Requirements tab to find matches.
        </p>
      </div>
    </div>

    <!-- Right rail -->
    <div class="flex flex-col gap-4">
      <div v-if="bestMatch" class="rounded-xl border border-n-weak bg-n-background p-4">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Best Match</h3>
        <p class="text-sm font-medium text-n-slate-12">{{ bestMatch.name }}</p>
        <p class="text-xs text-n-slate-8 mb-2">{{ bestMatch.score }} points</p>
        <ul v-if="bestMatch.reasons?.length" class="space-y-1">
          <li
            v-for="(r, i) in bestMatch.reasons"
            :key="i"
            class="text-xs text-n-slate-9 flex items-start gap-1"
          >
            <span class="i-lucide-check size-3 text-green-600 shrink-0 mt-0.5" />
            {{ r }}
          </li>
        </ul>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-background p-4">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">
          Shortlisted Properties<template v-if="shortlist.length"> ({{ shortlist.length }})</template>
        </h3>
        <p v-if="!shortlist.length" class="text-xs text-n-slate-8">
          Nothing shortlisted yet.
        </p>
        <div v-else class="space-y-2">
          <div
            v-for="m in shortlist"
            :key="m.project_id"
            class="flex items-center justify-between text-sm gap-2"
          >
            <span class="text-n-slate-12 truncate">{{ m.name }}</span>
            <div class="flex items-center gap-1 shrink-0">
              <button
                v-if="isSameProject(m)"
                class="text-green-600 hover:text-green-700"
                title="Share Documents"
                @click="shareMatch(m)"
              >
                <span class="i-lucide-send size-3.5" />
              </button>
              <button class="text-n-slate-7 hover:text-red-600" title="Remove" @click="toggleShortlist(m)">
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="bestMatch" class="rounded-xl border border-n-weak bg-n-background p-4">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Quick Actions</h3>
        <div class="grid grid-cols-2 gap-2">
          <button
            v-if="isSameProject(bestMatch)"
            class="flex flex-col items-center gap-1 rounded-lg border border-n-weak p-2.5 text-xs text-n-slate-11 hover:border-green-400"
            @click="shareMatch(bestMatch)"
          >
            <span class="i-lucide-message-circle size-4 text-green-600" />
            Share Best Match
          </button>
          <span
            v-else
            class="flex flex-col items-center justify-center gap-1 rounded-lg border border-n-weak p-2.5 text-[11px] text-n-slate-7 text-center opacity-60"
          >
            <span class="i-lucide-message-circle size-4" />
            Reassign project to share
          </span>
          <button
            class="flex flex-col items-center gap-1 rounded-lg border border-n-weak p-2.5 text-xs text-n-slate-11 hover:border-woot-400"
            @click="isVisitFormOpen = true"
          >
            <span class="i-lucide-calendar-check size-4 text-woot-600" />
            Schedule Site Visit
          </button>
        </div>
      </div>
    </div>

    <SiteVisitForm
      :is-open="isVisitFormOpen"
      :prefill-lead="lead"
      @close="isVisitFormOpen = false"
      @saved="isVisitFormOpen = false"
    />
  </div>
</template>
